import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import customFetch from '../../../api/customFetch.js';

export default function AdminRegulationsList() {
  const [regulations, setRegulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const nav = useNavigate();

  useEffect(() => {
    loadRegulations();
  }, []);

  const loadRegulations = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await customFetch.get('/regulations/admin');
      setRegulations(data.regulations || []);
    } catch (e) {
      setError('שגיאה בטעינת התקנונים');
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (reg, e) => {
    e.stopPropagation();
    try {
      await customFetch.put(`/regulations/admin/${reg._id}`, { isActive: !reg.isActive });
      setRegulations((prev) =>
        prev.map((r) => (r._id === reg._id ? { ...r, isActive: !r.isActive } : r))
      );
    } catch {
      setError('שגיאה בעדכון סטטוס');
    }
  };

  const handleDelete = async (reg, e) => {
    e.stopPropagation();
    if (!window.confirm(`למחוק את "${reg.title}"? פעולה זו תמחק גם את נתוני הקריאה.`)) return;
    try {
      await customFetch.delete(`/regulations/admin/${reg._id}`);
      setRegulations((prev) => prev.filter((r) => r._id !== reg._id));
    } catch {
      setError('שגיאה במחיקה');
    }
  };

  const filtered = regulations
    .filter((r) => {
      if (filterActive === 'active') return r.isActive;
      if (filterActive === 'inactive') return !r.isActive;
      return true;
    })
    .filter((r) => {
      if (!searchTerm.trim()) return true;
      return r.title.toLowerCase().includes(searchTerm.toLowerCase());
    });

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span>טוען תקנונים...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1 className="page-title">
          <span className="page-title-icon">📋</span>
          ניהול תקנונים
          <span className="badge info" style={{ marginRight: 8 }}>
            {filtered.length}
          </span>
        </h1>
        <div className="page-actions">
          <button className="admin-btn primary" onClick={() => nav('/admin/regulations/new')}>
            <span>➕</span>
            <span>תקנון חדש</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="admin-alert error">
          <span>⚠️</span>
          <span>{error}</span>
          <button className="alert-close" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <div className="filters-panel">
        <div className="filters-grid">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="admin-label">סינון לפי סטטוס</label>
            <select
              className="admin-select"
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
            >
              <option value="all">הכל ({regulations.length})</option>
              <option value="active">פעילים ({regulations.filter((r) => r.isActive).length})</option>
              <option value="inactive">לא פעילים ({regulations.filter((r) => !r.isActive).length})</option>
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="admin-label">חיפוש</label>
            <input
              type="text"
              className="admin-input"
              placeholder="חפש לפי שם תקנון..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0, alignSelf: 'end' }}>
            <button className="admin-btn secondary" onClick={loadRegulations}>
              🔄 רענן
            </button>
          </div>
        </div>
      </div>

      {/* Summary */}
      {regulations.length > 0 && (
        <div className="admin-card" style={{ marginTop: 24, marginBottom: 24 }}>
          <div className="card-header">
            <h3 className="card-title">📊 סיכום</h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 20, textAlign: 'center' }}>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--admin-primary)' }}>{regulations.length}</div>
              <div style={{ color: 'var(--admin-text-muted)' }}>סה"כ תקנונים</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--admin-success)' }}>
                {regulations.filter((r) => r.isActive).length}
              </div>
              <div style={{ color: 'var(--admin-text-muted)' }}>פעילים</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--admin-text-muted)' }}>
                {regulations.filter((r) => !r.isActive).length}
              </div>
              <div style={{ color: 'var(--admin-text-muted)' }}>לא פעילים</div>
            </div>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <h3>{regulations.length === 0 ? 'אין תקנונים במערכת' : 'לא נמצאו תקנונים התואמים לחיפוש'}</h3>
          {regulations.length === 0 && (
            <button className="admin-btn primary" onClick={() => nav('/admin/regulations/new')}>
              ➕ צור תקנון ראשון
            </button>
          )}
        </div>
      ) : (
        <div className="data-grid">
          {filtered.map((reg) => (
            <div
              key={reg._id}
              className="data-item"
              onClick={() => nav(`/admin/regulations/${reg._id}`)}
            >
              <div className="data-item-content">
                <h3>
                  {reg.title}
                  <span className={`badge ${reg.isActive ? 'success' : 'warning'}`}>
                    {reg.isActive ? '🟢 פעיל' : '⚪ לא פעיל'}
                  </span>
                  {reg.fileUrl && <span className="badge info">📎 קובץ</span>}
                  {reg.textContent && <span className="badge info">📄 טקסט</span>}
                </h3>

                {reg.description && (
                  <p style={{ color: 'var(--admin-text-muted)', margin: '4px 0', fontSize: '0.9rem' }}>
                    {reg.description}
                  </p>
                )}

                <div className="data-item-meta">
                  <span><strong>סקטורים:</strong> {reg.targetSectors?.join(', ')}</span>
                  <span>
                    <strong>נוצר:</strong> {new Date(reg.createdAt).toLocaleDateString('he-IL')}
                  </span>
                  {reg.createdBy && (
                    <span><strong>ע"י:</strong> {reg.createdBy.fullName}</span>
                  )}
                </div>
              </div>

              <div className="data-item-actions">
                <button
                  className="admin-btn primary sm"
                  onClick={(e) => { e.stopPropagation(); nav(`/admin/regulations/${reg._id}`); }}
                >
                  ✏️ ערוך
                </button>
                <button
                  className="admin-btn info sm"
                  onClick={(e) => { e.stopPropagation(); nav(`/admin/regulations/${reg._id}/stats`); }}
                >
                  📊 מעקב קריאה
                </button>
                <button
                  className={`admin-btn sm ${reg.isActive ? 'warning' : 'success'}`}
                  onClick={(e) => toggleActive(reg, e)}
                >
                  {reg.isActive ? '🔇 השבת' : '✅ הפעל'}
                </button>
                <button
                  className="admin-btn danger sm"
                  onClick={(e) => handleDelete(reg, e)}
                >
                  🗑️ מחק
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
